import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useCurrency } from '../contexts/CurrencyContext';

export default function EditLeadOffcanvas({ isOpen, onClose, lead, onSuccess }) {
  const currency = useCurrency();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    status: 'New Lead',
    source: '',
    value: 0,
    notes: '',
    assigned_to: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    interested_product_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [sources, setSources] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadSettings();
      loadTeamMembers();
      loadCatalogItems();
    }
  }, [isOpen]);

  useEffect(() => {
    if (lead && isOpen) {
      // Unified taxonomy: match status against pipeline_stages (the same list the
      // Kanban uses). Keep the lead's raw status if it isn't in the list so it's
      // never lost/blanked.
      const stageMatch = pipelineStages.find(s => s.name?.toLowerCase() === lead.status?.toLowerCase())?.name;
      const normalizedStatus = stageMatch || lead.status || 'New Lead';
      const normalizedSource = sources.find(s => s.name.toLowerCase() === lead.source?.toLowerCase())?.name || lead.source || '';
      
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        website: lead.website || '',
        status: normalizedStatus,
        source: normalizedSource,
        value: lead.value || 0,
        notes: lead.notes || '',
        assigned_to: lead.assigned_to || '',
        address: lead.address || '',
        city: lead.city || '',
        state: lead.state || '',
        postal_code: lead.postal_code || '',
        country: lead.country || '',
        interested_product_id: lead.interested_product_id || ''
      });
    }
  }, [lead, isOpen, pipelineStages, sources]);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const [statusesRes, sourcesRes, stagesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/lead-settings/statuses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/lead-settings/sources`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/lead-settings/stages`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStatuses(statusesRes.data || []);
      setSources(sourcesRes.data || []);
      setPipelineStages(stagesRes.data || []);
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const token = localStorage.getItem('access_token');
      // Permission-free endpoint so NON-admin teammates also get the assignee list
      // (/api/hr/users needs hr.users.view → empty dropdown for teammates).
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hr/team-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const arr = response.data?.members || response.data?.data || (Array.isArray(response.data) ? response.data : []);
      const users = arr.map(user => ({
        id: user.id,
        name: user.name,
        email: user.email
      }));
      setTeamMembers(users);
    } catch (error) {
      console.error('Failed to load team members:', error);
    }
  };

  const loadCatalogItems = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/items`, {
        params: { is_active: true, limit: 1000 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCatalogItems(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load catalog items:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter lead name');
      return;
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      // Guard against silent data loss: the dropdown-backed fields below are
      // populated from the lead on open. If the passed-in lead object didn't
      // carry one (e.g. interested_product_id / assigned_to), it stays '' and
      // would overwrite the stored value with null on save — wiping the lead's
      // Product/Service interest and Owner. So we only SEND these when they hold
      // a real value. Explicit clears still work: "Unassigned" sends the
      // non-empty 'unassigned' sentinel the backend maps to null.
      // interested_product_id is intentionally NOT stripped: the Select is now
      // stable (no remount key), so '' only means the user explicitly chose
      // "Let AI decide" — which must be allowed to clear the product.
      const payload = { ...formData };
      ['assigned_to', 'source', 'website',
       'address', 'city', 'state', 'postal_code', 'country'].forEach((k) => {
        if (payload[k] === '' || payload[k] == null) delete payload[k];
      });
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/leads/${lead.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Lead updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update lead:', error);
      const detail = error.response?.data?.detail;
      let msg = 'Failed to update lead';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        // Pydantic validation errors → surface the specific field + reason.
        const first = detail[0];
        const raw = first.loc?.slice(-1)[0] || 'field';
        const labels = { name: 'Name', email: 'Email', phone: 'Phone', value: 'Value', website: 'Website' };
        const field = labels[raw] || String(raw).replace(/_/g, ' ');
        msg = `${field}: ${first.msg}`;
      } else if (error.message) {
        msg = error.message;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen || !lead) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full md:w-[600px] bg-card shadow-xl z-50 overflow-y-auto" data-testid="edit-lead-offcanvas">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">Edit Lead</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={formData.company}
                onChange={(e) => handleChange('company', e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="website">Company Website</Label>
              <Input
                id="website"
                value={formData.website || ''}
                placeholder="acme.com"
                onChange={(e) => handleChange('website', e.target.value)}
                data-testid="lead-website-input-edit"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => { if (value) handleChange('status', value); }}>
                  <SelectTrigger data-testid="edit-lead-status-select">
                    <span className="truncate">{formData.status || 'Select status'}</span>
                  </SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const names = (pipelineStages.length > 0 ? pipelineStages.map(s => s.name) : ['New Lead', 'Contacted', 'Qualified', 'Negotiation', 'Won', 'Lost']);
                      const list = formData.status && !names.includes(formData.status) ? [formData.status, ...names] : names;
                      return list.map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="source">Source</Label>
                <Select value={formData.source} onValueChange={(value) => handleChange('source', value)}>
                  <SelectTrigger data-testid="edit-lead-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {sources.length > 0 ? (
                      sources.map((source) => (
                        <SelectItem key={source.id} value={source.name}>
                          {source.name}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="referral">Referral</SelectItem>
                        <SelectItem value="social_media">Social Media</SelectItem>
                        <SelectItem value="cold_call">Cold Call</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="value">Value ({currency.symbol})</Label>
                <Input
                  id="value"
                  type="tel" inputMode="decimal" onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
                  min="0"
                  step="0.01"
                  value={formData.value}
                  onChange={(e) => handleChange('value', parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label htmlFor="assigned_to">Assigned To</Label>
                <Select value={formData.assigned_to} onValueChange={(value) => handleChange('assigned_to', value)}>
                  <SelectTrigger data-testid="edit-lead-assigned-select">
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {teamMembers.map((member) => (
                      <SelectItem key={member.id} value={member.id}>
                        {member.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="interested_product">Product / Service interest <span className="text-muted-foreground text-xs font-normal">(optional — AI auto-matches if blank)</span></Label>
              <Select
                value={formData.interested_product_id || '__none__'}
                onValueChange={(value) => handleChange('interested_product_id', value === '__none__' ? '' : value)}
              >
                <SelectTrigger data-testid="edit-lead-product-select">
                  <SelectValue placeholder={catalogItems.length ? 'Let AI decide (recommended)' : 'No products/services in catalog yet'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Let AI decide (recommended)</SelectItem>
                  {formData.interested_product_id
                    && !catalogItems.some((it) => it.id === formData.interested_product_id) && (
                    <SelectItem value={formData.interested_product_id} data-testid="edit-lead-product-current">
                      {lead.interested_product_name || 'Current selection'}
                    </SelectItem>
                  )}
                  {catalogItems.map((item) => (
                    <SelectItem key={item.id} value={item.id} data-testid={`edit-lead-product-option-${item.id}`}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">Address Information</h3>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">State/Province</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleChange('state', e.target.value)}
                      placeholder="NY"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code">Postal Code</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => handleChange('postal_code', e.target.value)}
                      placeholder="10001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      placeholder="United States"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={4}
                placeholder="Add any notes about this lead"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="flex-1" disabled={loading} data-testid="edit-lead-submit-button">
                {loading ? 'Updating...' : 'Update Lead'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
